import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import Sidebar from '../Components/Sidebar';
import MobileNav from '../Components/MobileNav'; // ADD THIS
import { useIsMobile } from '../hooks/useMediaQuery'; // ADD THIS
import { useNavigate } from 'react-router-dom';
import Loader from '../Components/Loader';
import { useLanguage } from '../context/LanguageContext';

export default function Profile() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isMobile = useIsMobile(); // ADD THIS
  
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [formData, setFormData] = useState({
    avatarName: '',
    rank: '',
    token: '',
    clan: '',
    avatar_url: '',
    kills: 0,
    deaths: 0,
    suicides: 0,
    betrayals: 0,
    current_award: false
  });

  const ranks = [
    'PRIVATE', 'PRIVATE FIRST CLASS', 'CORPORAL', 'SERGEANT', 'STAFF SERGEANT',
    'SERGEANT FIRST CLASS', '1ST SERGEANT', 'SERGEANT MAJOR', 'SECOND LIEUTENANT',
    'FIRST LIEUTENANT', 'CAPTAIN', 'MAJOR', 'LIEUTENANT COLONEL', 'COLONEL',
    'LIEUTENANT GENERAL', 'GENERAL', 'ARMY GENERAL', 'COMMANDER-IN-CHIEF'
  ];

  const clans = [
    'MINI MILITIA CLAN 254', 'UNITY PLAYERS CLAN {UPC}', 'BATTLE ROYALE SQUADS {BRS}',
    'GREAT MINI MILITIA CLAN {GMMC}', 'SQUAD BUSTER {SB}', 'MINI MILITIA LEGENDS {MML}',
    'BLACK SHADOW SQUAD {BSS}', 'MINI MILITIA SQUADS {MMS}', 'DOMINANT PLAYERS CLAN {DPC}',
    'THE BUNNIES {FK}', 'ELITE ASSASSIN\'S SQUAD {EAS}', 'G CLAN',
    'MINI MAYHEM MAKERS {MMM}', 'SNIPER GANGS {SG}', 'SNIPER KINGS {SK}',
    'PRIME CLAN', 'EXPERIENCE KILLERS {EK}', 'BS CLAN', 'HOT CLAN',
    'THE TERMINATOR CLAN', 'FEARLESS FIGHTING FORCE {FFF}', 'THE LORDS SNIPERS {TLS}',
    'GHOST CLAN', 'BHOCO CLAN', 'FRENCHIE CLAN', 'MINI MILITIA GODS {MMG}',
    'SNIPER GANG GODS {SGG}', 'KINGS CLAN', 'V X CLAN', 'FURY OF FALLEN',
    'BADDIES CLAN', 'THE NOOBS KILLER {TLK}', 'THE GHOST CLAN', 'THE MONARCHS {TM}',
    'LEGION OF GAMERS {LG}', 'THE BLOOD LINE {TBL}', 'MINI MILITIA CLASSIC MAYHEM {MMCM}',
    'THE LEGENDARY GODS {TLG}', 'MINI MILITIA MERCENARIES {MMM}', 'THE LETHAL EAGLES OF MMC {TLE}',
    'ANGOLA SQUAD {A.S}', 'cRN', 'THE LEADING KINGS OF MMC {TLK}',
    'INTENSE DEADLY KILLERS {IDK}', 'MINI MILITIA DIAMONDS {MMD}', 'THE DARK KNIGHT {TDK}'
  ];

  const AWARD_NAME = 'Alpha Medal of Honor';

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/login');
      return;
    }
    setUser(user);
    await fetchProfile(user.id);
  };

  async function fetchProfile(uid) {
    setLoading(true);
    const { data: igd } = await supabase
      .from('igd')
      .select('*')
      .eq('user_id', uid)
      .maybeSingle();

    if (igd) {
      setFormData({
        avatarName: igd.avatarName || '',
        rank: igd.rank || '',
        token: igd.token || '',
        clan: igd.clan || '',
        avatar_url: igd.avatar_url || '',
        kills: igd.kills || 0,
        deaths: igd.deaths || 0,
        suicides: igd.suicides || 0,
        betrayals: igd.betrayals || 0,
        current_award: igd.current_award || false
      });
    }
    setLoading(false);
  }

  async function handleAvatarUpload(e) {
    const file = e.target.files[0];
    if (!file || !user) return;

    if (formData.avatar_url) {
      const oldFileName = formData.avatar_url.split('/').pop();
      await supabase.storage.from('avatars').remove([oldFileName]);
    }

    const fileName = `${user.id}-${Date.now()}`;
    const { error } = await supabase.storage
      .from('avatars')
      .upload(fileName, file);

    if (error) {
      setMessage('❌ Upload failed: ' + error.message);
      return;
    }

    const { data } = supabase.storage
      .from('avatars')
      .getPublicUrl(fileName);

    setFormData(prev => ({ ...prev, avatar_url: data.publicUrl }));
    setMessage('✓ Avatar uploaded!');
  }

  async function handleSave() {
    if (!formData.avatarName.trim()) {
      setMessage('❌ Please enter your avatar name');
      return;
    }

    setSaving(true);
    setMessage('');

    const { error } = await supabase.from('igd').upsert({
      user_id: user.id,
      avatarName: formData.avatarName,
      rank: formData.rank,
      token: parseInt(formData.token) || 0,
      clan: formData.clan,
      avatar_url: formData.avatar_url,
      kills: parseInt(formData.kills) || 0,
      deaths: parseInt(formData.deaths) || 0,
      suicides: parseInt(formData.suicides) || 0,
      betrayals: parseInt(formData.betrayals) || 0,
      current_award: formData.current_award
    }, {
      onConflict: 'user_id'
    });

    if (error) {
      setMessage('❌ Error saving: ' + error.message);
      console.error('Save error:', error);
    } else {
      setMessage('✓ Profile saved successfully!');
    }
    
    setSaving(false);
  }

  const inputField = (label, value, onChange, type = 'text', placeholder = '') => (
    <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
      <label style={{
        display: 'block',
        color: '#64748b',
        fontSize: isMobile ? '11px' : '12px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: isMobile ? '6px' : '8px',
        fontWeight: '600'
      }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: isMobile ? '12px 14px' : '14px 16px',
          borderRadius: '12px',
          border: '1px solid var(--border)',
          background: 'var(--input-bg)',
          color: 'var(--text)',
          fontSize: '16px', // Prevents iOS zoom
          outline: 'none',
          transition: 'all 0.2s',
          minHeight: '48px', // Touch target
          boxSizing: 'border-box',
        }}
        onFocus={e => e.target.style.borderColor = '#38bdf8'}
        onBlur={e => e.target.style.borderColor = 'var(--border)'}
      />
    </div>
  );

  const selectField = (label, value, onChange, options) => (
    <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
      <label style={{
        display: 'block',
        color: '#64748b',
        fontSize: isMobile ? '11px' : '12px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: isMobile ? '6px' : '8px',
        fontWeight: '600'
      }}>
        {label}
      </label>
      <select
        value={value}
        onChange={onChange}
        style={{
          width: '100%',
          padding: isMobile ? '12px 14px' : '14px 16px',
          borderRadius: '12px',
          border: '1px solid var(--border)',
          background: 'var(--input-bg)',
          color: 'var(--text)',
          fontSize: '16px',
          outline: 'none',
          cursor: 'pointer',
          minHeight: '48px', // Touch target
          boxSizing: 'border-box',
        }}
      >
        <option value="">Select {label}</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );

  const statInput = (label, value, onChange, color, icon) => (
    <div style={{
      background: 'var(--card-bg)',
      padding: isMobile ? '16px' : '20px',
      borderRadius: isMobile ? '12px' : '16px',
      border: `2px solid ${color}`,
      textAlign: 'center'
    }}>
      <div style={{ fontSize: isMobile ? '24px' : '28px', marginBottom: isMobile ? '6px' : '8px' }}>{icon}</div>
      <div style={{ 
        fontSize: isMobile ? '11px' : '12px', 
        color: '#64748b', 
        textTransform: 'uppercase', 
        marginBottom: isMobile ? '8px' : '10px',
        letterSpacing: '0.5px',
      }}>
        {label}
      </div>
      <input
        type="number"
        min="0"
        value={value}
        onChange={onChange}
        style={{
          width: '100%',
          padding: isMobile ? '8px' : '10px',
          borderRadius: '8px',
          border: `2px solid ${color}`,
          background: 'var(--bg)',
          color: 'var(--text)',
          fontSize: isMobile ? '20px' : '24px',
          fontWeight: 'bold',
          textAlign: 'center',
          minHeight: '44px', // Touch target
          boxSizing: 'border-box',
        }}
      />
    </div>
  );

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        background: 'var(--bg)', 
        minHeight: '100vh',
        flexDirection: isMobile ? 'column' : 'row',
      }}>
        {!isMobile && <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />}
        <div style={{ 
          flex: 1, 
          marginLeft: isMobile ? '0' : (collapsed ? '80px' : '280px'),
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: isMobile ? '20px' : '0',
        }}>
          <Loader size={isMobile ? 80 : 120} />
        </div>
        {isMobile && <MobileNav />}
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      background: 'var(--bg)', 
      minHeight: '100vh',
      color: 'var(--text)',
      flexDirection: isMobile ? 'column' : 'row',
    }}>
      {!isMobile && <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />}

      <div style={{
        flex: 1,
        marginLeft: isMobile ? '0' : (collapsed ? '80px' : '280px'),
        padding: isMobile ? '16px' : '40px',
        transition: 'margin-left 0.3s ease',
        maxWidth: isMobile ? '100%' : '800px',
        overflowX: 'hidden',
        paddingBottom: isMobile ? '90px' : '40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: isMobile ? '20px' : '30px' }}>
          <h1 style={{ 
            fontSize: isMobile ? '24px' : '32px', 
            fontWeight: 'bold', 
            marginBottom: isMobile ? '6px' : '10px',
            lineHeight: '1.3',
          }}>
            🎮 Setup Your Profile
          </h1>
          <p style={{ 
            color: '#64748b', 
            fontSize: isMobile ? '14px' : '16px',
            lineHeight: '1.5',
          }}>
            Customize your soldier profile and combat stats
          </p>
        </div>

        {message && (
          <div style={{
            padding: isMobile ? '12px 16px' : '15px 20px',
            borderRadius: '12px',
            marginBottom: isMobile ? '16px' : '20px',
            background: message.includes('✓') ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            color: message.includes('✓') ? '#22c55e' : '#ef4444',
            border: `1px solid ${message.includes('✓') ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            fontSize: isMobile ? '14px' : '14px',
            lineHeight: '1.5',
          }}>
            {message}
          </div>
        )}

        {/* Avatar Upload */}
        <div style={{
          background: 'var(--card-bg)',
          padding: isMobile ? '24px' : '30px',
          borderRadius: isMobile ? '16px' : '20px',
          border: '1px solid var(--border)',
          marginBottom: isMobile ? '20px' : '30px',
          textAlign: 'center'
        }}>
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: '15px' }}>
            {formData.avatar_url ? (
              <img
                src={formData.avatar_url}
                alt="avatar"
                style={{
                  width: isMobile ? '100px' : '120px',
                  height: isMobile ? '100px' : '120px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: isMobile ? '3px solid #38bdf8' : '4px solid #38bdf8',
                }}
              />
            ) : (
              <div style={{
                width: isMobile ? '100px' : '120px',
                height: isMobile ? '100px' : '120px',
                borderRadius: '50%',
                background: 'var(--bg)',
                border: isMobile ? '3px dashed #475569' : '4px dashed #475569',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '36px' : '48px',
                color: '#475569',
              }}>
                ?
              </div>
            )}
            <label style={{
              position: 'absolute',
              bottom: '0',
              right: '0',
              width: isMobile ? '36px' : '40px',
              height: isMobile ? '36px' : '40px',
              borderRadius: '50%',
              background: '#38bdf8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: isMobile ? '18px' : '20px',
              minHeight: '36px', // Touch target
            }}>
              📷
              <input type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display: 'none' }} />
            </label>
          </div>
          <p style={{ color: '#64748b', fontSize: isMobile ? '13px' : '14px' }}>
            Tap camera to upload avatar
          </p>
        </div>

        {/* Basic Info */}
        <div style={{
          background: 'var(--card-bg)',
          padding: isMobile ? '20px' : '30px',
          borderRadius: isMobile ? '16px' : '20px',
          border: '1px solid var(--border)',
          marginBottom: isMobile ? '20px' : '30px',
        }}>
          <h3 style={{ 
            fontSize: isMobile ? '16px' : '18px', 
            fontWeight: 'bold', 
            marginBottom: isMobile ? '16px' : '20px', 
            color: '#38bdf8',
          }}>
            👤 Basic Information
          </h3>
          
          {inputField('Avatar Name', formData.avatarName, 
            e => setFormData({...formData, avatarName: e.target.value}), 
            'text', 'Enter your soldier name'
          )}
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
            gap: isMobile ? '0' : '15px',
          }}>
            {selectField('Rank', formData.rank, 
              e => setFormData({...formData, rank: e.target.value}), 
              ranks
            )}
            {selectField('Clan', formData.clan, 
              e => setFormData({...formData, clan: e.target.value}), 
              clans
            )}
          </div>

          {inputField('Tokens', formData.token, 
            e => setFormData({...formData, token: e.target.value}), 
            'number', 'e.g., 1000'
          )}
        </div>

        {/* Combat Stats */}
        <div style={{
          background: 'var(--card-bg)',
          padding: isMobile ? '20px' : '30px',
          borderRadius: isMobile ? '16px' : '20px',
          border: '1px solid var(--border)',
          marginBottom: isMobile ? '20px' : '30px',
        }}>
          <h3 style={{ 
            fontSize: isMobile ? '16px' : '18px', 
            fontWeight: 'bold', 
            marginBottom: isMobile ? '16px' : '20px', 
            color: '#ef4444',
          }}>
            ⚔️ Combat Statistics
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: isMobile ? '10px' : '15px',
          }}>
            {statInput('Kills', formData.kills, 
              e => setFormData({...formData, kills: e.target.value}), 
              '#22c55e', '⚔️'
            )}
            {statInput('Deaths', formData.deaths, 
              e => setFormData({...formData, deaths: e.target.value}), 
              '#ef4444', '💀'
            )}
            {statInput('Suicides', formData.suicides, 
              e => setFormData({...formData, suicides: e.target.value}), 
              '#8b5cf6', '💔'
            )}
            {statInput('Betrayals', formData.betrayals, 
              e => setFormData({...formData, betrayals: e.target.value}), 
              '#f97316', '🔪'
            )}
          </div>
        </div>

        {/* Single Award Option */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
          borderRadius: isMobile ? '16px' : '20px',
          border: '2px solid rgba(251, 191, 36, 0.3)',
          padding: isMobile ? '20px' : '30px',
          marginBottom: isMobile ? '20px' : '30px',
        }}>
          <h3 style={{ 
            fontSize: isMobile ? '16px' : '18px', 
            fontWeight: 'bold', 
            marginBottom: isMobile ? '16px' : '20px', 
            color: '#fbbf24',
          }}>
            🏆 Award
          </h3>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '12px' : '15px',
            padding: isMobile ? '16px' : '20px',
            background: formData.current_award ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' : 'var(--card-bg)',
            borderRadius: '12px',
            border: formData.current_award ? 'none' : '2px dashed #64748b',
            cursor: 'pointer',
            minHeight: '48px', // Touch target
          }}
          onClick={() => setFormData({...formData, current_award: !formData.current_award})}
          >
            <div style={{
              width: isMobile ? '22px' : '24px',
              height: isMobile ? '22px' : '24px',
              borderRadius: '6px',
              border: '2px solid',
              borderColor: formData.current_award ? '#000' : '#64748b',
              background: formData.current_award ? '#000' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '14px' : '16px',
              color: formData.current_award ? '#fbbf24' : 'transparent',
              flexShrink: 0,
            }}>
              {formData.current_award && '✓'}
            </div>
            <span style={{
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: 'bold',
              color: formData.current_award ? '#000' : '#64748b',
              lineHeight: '1.3',
            }}>
              {AWARD_NAME}
            </span>
          </div>
          
          <p style={{ 
            color: '#64748b', 
            fontSize: isMobile ? '12px' : '13px', 
            marginTop: isMobile ? '12px' : '15px',
            lineHeight: '1.5',
          }}>
            Click to claim your award
          </p>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: '100%',
            padding: isMobile ? '16px' : '18px',
            borderRadius: '14px',
            border: 'none',
            background: 'linear-gradient(135deg, #38bdf8 0%, #818cf8 100%)',
            color: '#000',
            fontSize: isMobile ? '17px' : '18px',
            fontWeight: 'bold',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
            transition: 'all 0.3s',
            marginBottom: '15px',
            minHeight: '52px', // Touch target
          }}
        >
          {saving ? '💾 Saving...' : '💾 Save Profile'}
        </button>

        <button
          onClick={() => navigate('/home')}
          style={{
            width: '100%',
            padding: isMobile ? '14px' : '15px',
            borderRadius: '14px',
            border: '1px solid #64748b',
            background: 'transparent',
            color: '#64748b',
            fontSize: isMobile ? '15px' : '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            minHeight: '48px', // Touch target
          }}
        >
          ← Back to Dashboard
        </button>
      </div>
      
      {isMobile && <MobileNav />}
    </div>
  );
}